import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Form, Spinner, Table } from 'react-bootstrap';
import { CSVLink } from 'react-csv';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { getMarketingReport } from '../../../services/OrdersService';
import { formatEuroForLanguage } from '../../../utils/i18nHelpers';

const EMPTY = { rows: [], months: [], totals: { orders: 0, value: 0, cost: 0 } };

const productsText = (products) => (products || []).map((p) => `${p.quantity}× ${p.name}`).join(', ');

export default function MarketingReport() {
    const { t, i18n } = useTranslation();
    const [scope, setScope] = useState('all'); // all | real | test
    const [loading, setLoading] = useState(true);
    const [report, setReport] = useState(EMPTY);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        getMarketingReport(scope)
            .then((res) => { if (alive) setReport({ ...EMPTY, ...(res.data || {}) }); })
            .catch((err) => {
                if (!alive) return;
                setReport(EMPTY);
                toast.error(err.response?.data?.error || t('marketingReport.errors.load'));
            })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [scope, t]);

    const euro = (v) => formatEuroForLanguage(v, i18n.language);

    const csvHeaders = useMemo(() => [
        { label: t('marketingReport.columns.date'), key: 'date' },
        { label: t('marketingReport.columns.order'), key: 'order' },
        { label: t('marketingReport.columns.coupon'), key: 'coupon' },
        { label: t('marketingReport.columns.products'), key: 'products' },
        { label: t('marketingReport.columns.value'), key: 'value' },
        { label: t('marketingReport.columns.cost'), key: 'cost' },
        { label: t('marketingReport.columns.document'), key: 'document' },
        { label: t('marketingReport.columns.test'), key: 'test' },
    ], [t]);

    // Order rows first, then one total row per month, then the grand total.
    const csvData = useMemo(() => {
        const rows = report.rows.map((r) => ({
            date: r.date || '', order: r.external_name || r.order_number, coupon: (r.coupon_codes || []).join(' '),
            products: productsText(r.products), value: r.value.toFixed(2), cost: r.cost.toFixed(2),
            document: r.document_number || '', test: r.is_test ? t('common.yes') : t('common.no'),
        }));
        const total = (label, orders, value, cost) => ({
            date: label, order: t('marketingReport.ordersCount', { count: orders }), coupon: '', products: '',
            value: value.toFixed(2), cost: cost.toFixed(2), document: '', test: '',
        });
        return [
            ...rows,
            ...report.months.map((m) => total(m.month, m.orders, m.value, m.cost)),
            total(t('marketingReport.total'), report.totals.orders, report.totals.value, report.totals.cost),
        ];
    }, [report, t]);

    return (
        <div className="row">
            <div className="col-xl-12">
                <Card>
                    <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <div>
                            <h4 className="heading mb-0">{t('marketingReport.title')}</h4>
                            <small className="text-muted">{t('marketingReport.subtitle')}</small>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <Form.Select
                                size="sm"
                                aria-label={t('marketingReport.scope.label')}
                                value={scope}
                                onChange={(e) => setScope(e.target.value)}
                            >
                                <option value="all">{t('marketingReport.scope.all')}</option>
                                <option value="real">{t('marketingReport.scope.real')}</option>
                                <option value="test">{t('marketingReport.scope.test')}</option>
                            </Form.Select>
                            <CSVLink
                                headers={csvHeaders}
                                data={csvData}
                                filename="tabliya-marketing-orders.csv"
                                className="btn btn-primary light btn-sm text-nowrap"
                            >
                                <i className="fa-solid fa-file-excel" /> {t('marketingReport.exportCsv')}
                            </CSVLink>
                        </div>
                    </Card.Header>
                    <Card.Body>
                        {loading ? (
                            <div className="text-center py-5"><Spinner animation="border" size="sm" /></div>
                        ) : (
                            <>
                                <h6>{t('marketingReport.perMonth')}</h6>
                                <Table responsive size="sm" className="mb-4" data-testid="marketing-months">
                                    <thead>
                                        <tr>
                                            <th>{t('marketingReport.columns.month')}</th>
                                            <th className="text-end">{t('marketingReport.columns.orders')}</th>
                                            <th className="text-end">{t('marketingReport.columns.value')}</th>
                                            <th className="text-end">{t('marketingReport.columns.cost')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {report.months.length === 0 && (
                                            <tr><td colSpan="4" className="text-center text-muted">{t('marketingReport.empty')}</td></tr>
                                        )}
                                        {report.months.map((m) => (
                                            <tr key={m.month}>
                                                <td>{m.month}</td>
                                                <td className="text-end">{m.orders}</td>
                                                <td className="text-end">{euro(m.value)}</td>
                                                <td className="text-end">{euro(m.cost)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    {report.months.length > 0 && (
                                        <tfoot>
                                            <tr className="fw-bold">
                                                <td>{t('marketingReport.total')}</td>
                                                <td className="text-end">{report.totals.orders}</td>
                                                <td className="text-end">{euro(report.totals.value)}</td>
                                                <td className="text-end">{euro(report.totals.cost)}</td>
                                            </tr>
                                        </tfoot>
                                    )}
                                </Table>

                                <Table responsive hover size="sm" className="mb-0" data-testid="marketing-rows">
                                    <thead>
                                        <tr>
                                            <th>{t('marketingReport.columns.date')}</th>
                                            <th>{t('marketingReport.columns.order')}</th>
                                            <th>{t('marketingReport.columns.coupon')}</th>
                                            <th>{t('marketingReport.columns.products')}</th>
                                            <th className="text-end">{t('marketingReport.columns.value')}</th>
                                            <th className="text-end">{t('marketingReport.columns.cost')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {report.rows.map((r) => (
                                            <tr key={r.order_id}>
                                                <td>{r.date || '-'}</td>
                                                <td>
                                                    <Link to={`/orders/${r.order_id}`}>#{r.order_number}</Link>
                                                    {r.external_name && <span className="text-muted small ms-1">{r.external_name}</span>}
                                                    {r.is_test && <span className="badge order-flag-badge order-flag-badge--test">{t('orders.flags.test')}</span>}
                                                </td>
                                                <td>{(r.coupon_codes || []).join(', ') || '-'}</td>
                                                <td>{productsText(r.products) || '-'}</td>
                                                <td className="text-end">{euro(r.value)}</td>
                                                <td className="text-end">{euro(r.cost)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </>
                        )}
                    </Card.Body>
                </Card>
            </div>
        </div>
    );
}
